import { useWaitingStore } from '../../waiting-context';
import styles from '../styles.module.scss';
import { PartLookUpResult } from 'api/generated';
import clsx from 'clsx';
import { InlineSvg } from 'components/svg';
import { useGetCommonBrandsQuery } from 'features/vhc/api/use-get-common-brands.query';
import { usePartRequestsPartsLookupQuery } from 'features/vhc/api/use-part-pequests-parts-lookup.query';
import { useVhcRecordStoreStore } from 'features/vhc/modals/record/vhc-record.store';
import { SelectPartModal } from 'features/vhc/modals/select-part/modal';
import { VhcItem } from 'features/vhc/model';
import { useVhcStore } from 'features/vhc/model/vhc-store';
import useToggle from 'hooks/use-toggle';
import { useEffect, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';

export const PartsSection = () => {
  const { isWaiting, setIsWaiting } = useWaitingStore();
  const { state: vhcRecordState } = useVhcRecordStoreStore();
  const {
    control,
    setValue,
    getValues,
    watch,
    trigger,
    setError,
    formState: { errors }
  } = useFormContext<VhcItem>();

  const { data, isFetching } = useGetCommonBrandsQuery();
  const brandCodes = data?.map((x) => x.code);

  const {
    fields: parts,
    append: appendPart,
    remove: removePart
  } = useFieldArray<VhcItem>({
    control,
    name: 'parts'
  });

  const localBrandCodes = useRef<string[]>([]);
  const { id } = useParams();
  const { recordModalData } = useVhcStore();
  const { isReport, isComplete } = recordModalData || {};

  const getBrandCode = (index: number): string => {
    if (!brandCodes?.length || index < 0) return '';

    while (index > localBrandCodes.current.length - 1) {
      localBrandCodes.current.push(brandCodes[0]!);
    }

    return localBrandCodes.current[index];
  };

  const applySelectedPart = (part: PartLookUpResult, index: number) => {
    if (!part?.info) return;
    setValue(`parts.${index}.code`, part.info.partCode ?? '');
    setValue(`parts.${index}.description`, part.info.description ?? '');
    trigger(`parts.${index}.description`);
  };

  const openSelectPartsModelToggleState = useToggle<PartLookUpResult[]>(false);

  const [params, setParams] = useState<{ index: number; brandCode: string; partNumber: string }>();

  const { isFetching: isLookupPartCodeDataDisabled, refetch: refetchPartRequestsPartsLookupQuery } =
    usePartRequestsPartsLookupQuery(
      {
        brandCode: params?.brandCode,
        partNumber: params?.partNumber,
        branch: vhcRecordState?.brunch
      },
      undefined,
      {
        enabled: false,
        onSuccess: (data) => {
          if (!params || !data?.length) return;
          if (data.length > 1) {
            openSelectPartsModelToggleState.setData(data);
            openSelectPartsModelToggleState.open();
          } else {
            applySelectedPart(data[0], params.index);
          }
        }
      }
    );

  useEffect(() => {
    setIsWaiting(isLookupPartCodeDataDisabled);
  }, [isLookupPartCodeDataDisabled]);

  const handleLookupPartCodeData = async (index?: number) => {
    if (index === undefined) return;

    const code = getValues(`parts.${index}.code`);
    if (!code) return;

    setParams({
      index,
      brandCode: getBrandCode(index),
      partNumber: code.toString()
    });

    setTimeout(() => {
      refetchPartRequestsPartsLookupQuery();
    }, 100);
  };

  return (
    <>
      {openSelectPartsModelToggleState.opened && params?.index != null && (
        <SelectPartModal
          state={openSelectPartsModelToggleState}
          onSelectPart={(part) => {
            applySelectedPart(part, params.index);
          }}
        />
      )}
      {parts.length > 0 && (
        <div className="row align-items-center mb-2 d-flex gap-4">
          <span className="d-none d-lg-block col-4 px-0 text-gray-900">Part Code {isReport ? '' : '(optional)'}</span>
          <span className="d-none d-lg-block col-5 px-0  text-gray-900">Part Description</span>
          <span className="d-none d-lg-block col-1 px-0 ms-11 text-gray-900">Quantity</span>
          <span className="d-none d-lg-block col px-0 text-gray-900"></span>
        </div>
      )}
      {parts?.map((part, index) => (
        <div key={part.id} className={clsx(styles.mobileDashedBorder, styles.labourPartRow, 'row d-flex')}>
          <div className="col-12 col-lg px-0 mb-2 mb-lg-0">
            <div className="d-block d-lg-flex my-2">
              <span className=" d-lg-none col-2 px-0 text-gray-900">Part number (optional)</span>
              <div className="col-lg-5 col-12 px-0 d-flex gap-2 mx-0 me-lg-2 mb-3 mb-lg-0 h-35px">
                <div className="position-relative">
                  <Form.Select
                    value={getBrandCode[index]}
                    onChange={(e) => {
                      localBrandCodes.current[index] = e.target.value;
                    }}
                    className="form-select form-select-solid ps-3 py-2"
                    style={{ width: '60px' }}
                    disabled={isComplete || isFetching}
                  >
                    {brandCodes?.map((x) => (
                      <option key={x} value={x}>
                        {x}
                      </option>
                    ))}
                  </Form.Select>
                </div>
                <Controller
                  name={`parts.${index}.code`}
                  control={control}
                  render={({ field }) => (
                    <div className="col position-relative">
                      <Form.Control
                        {...field}
                        value={field.value ?? undefined}
                        className="form-control-solid fw-bold py-2 w-100 w-lg-100"
                        placeholder="Part number"
                        disabled={isComplete}
                        onBlur={() => {
                          field.onBlur();
                          trigger(`parts.${index}.description`);
                        }}
                      />
                    </div>
                  )}
                />
                <button
                  type="button"
                  disabled={isLookupPartCodeDataDisabled || isReport || isComplete || !watch(`parts.${index}.code`)}
                  onClick={() => handleLookupPartCodeData(index)}
                  className="btn p-0 btn-secondary"
                >
                  <div
                    className="d-flex align-items-center justify-content-center"
                    style={{ width: '38px', height: '38px' }}
                  >
                    <InlineSvg
                      path="/media/icons/duotune/general/gen021.svg"
                      className="svg-icon-1 me-0"
                      svgClassName="w-100 h-100 text-white"
                    />
                  </div>
                </button>
              </div>
              <span className="d-lg-none col-2 px-0 text-gray-900">Part description</span>
              <Controller
                name={`parts.${index}.description`}
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <div className="col position-relative">
                    <Form.Control
                      {...field}
                      className={`col form-control-solid py-2 fw-bold w-100 ${error ? 'border-danger' : ''}`}
                      placeholder="Part description"
                      disabled={isComplete}
                      isInvalid={!!error}
                      onBlur={(e) => {
                        if (!e.currentTarget.value?.trim().length) {
                          setError(`parts.${index}.description`, {
                            message: 'Part description is required'
                          });
                        } else {
                          field.onBlur();
                          trigger(`parts.${index}.description`);
                        }
                      }}
                    />
                    {error && <div className={styles.errorMessage}>{error.message}</div>}
                  </div>
                )}
              />
            </div>
          </div>
          <span className="d-lg-none col-2 px-0 text-gray-900">Quantity</span>
          <div
            className="d-flex g-0 col-12 px-0 ms-lg-4 col-lg-2 mb-3 mb-lg-0 my-2 h-35px justify-content-between"
            style={{ verticalAlign: 'top' }}
          >
            <Controller
              name={`parts.${index}.quantity`}
              control={control}
              render={({ field }) => (
                <div className="position-relative">
                  <Form.Control
                    {...field}
                    disabled={isComplete}
                    onKeyDown={(e) => {
                      if (
                        !(
                          (e.key >= '0' && e.key <= '9') ||
                          ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)
                        )
                      ) {
                        e.preventDefault();
                      }
                    }}
                    onInput={(e) => {
                      e.currentTarget.value = e.currentTarget.value.replace(/[^0-9]/g, '');
                      if (e.currentTarget.value === '0') {
                        e.currentTarget.value = '1';
                      } else if (e.currentTarget.value.startsWith('0') && e.currentTarget.value.length > 1) {
                        e.currentTarget.value = e.currentTarget.value.replace(/^0+/, '');
                      }
                      field.onChange(e);
                    }}
                    onBlur={(e) => {
                      if (e.currentTarget.value === '0' || e.currentTarget.value === '') {
                        e.currentTarget.value = '1';
                        field.onChange(e);
                      }
                    }}
                    placeholder="1"
                    type="number"
                    className={`w-100 form-control form-control-solid py-2 fw-bold ${
                      errors.parts?.[index]?.quantity?.message ? 'border-danger' : ''
                    }`}
                  />
                </div>
              )}
            />
            <span className="col-2 px-0 mx-2 d-flex gap-1 align-content-center">
              {!isReport && !isComplete && (
                <button
                  type="button"
                  className="col-auto p-0 border-0 bg-transparent ms-auto"
                  onClick={() => removePart(index)}
                >
                  <InlineSvg
                    path="/media/icons/duotune/general/gen040.svg"
                    svgClassName="w-30px h-30px text-secondary"
                  />
                </button>
              )}
            </span>
          </div>
        </div>
      ))}
      {!isReport && !isComplete && (
        <div className="row">
          <div className="col-8 px-0">
            <button
              type="button"
              disabled={!!errors?.parts?.length || isWaiting}
              className="btn btn-secondary px-4 py-2 d-flex align-items-center gap-2"
              onClick={() => {
                appendPart({
                  code: '',
                  description: '',
                  quantity: 1
                });
              }}
            >
              <InlineSvg
                path="/media/icons/custom/plus-circle.svg"
                className="svg-icon-2 me-0"
                svgClassName="w-20px h-20px"
              />
              <span className="fw-medium text-white fs-6">Add</span>
            </button>
          </div>

          <div style={{ borderBottom: '1px solid #dee2e6', paddingBottom: '1rem' }} />
        </div>
      )}
    </>
  );
};
